import moment from "moment";

// Markup for Google Hotel Ads' price accuracy crawler. The bot opens our
// Hotel Ads landing URL, clicks only the critical-path controls in
// interaction order, stops on the final stage (last screen before payment)
// and reads the schema.org price there, which must match our price feed.
// Keep these in place when changing the booking UI, or the Hotel Center
// score drops. Spec:
// https://developers.google.com/hotels/hotel-prices/structured-data/hotel-price-structured-data

// a control the guest must click to move forward; on the room list the
// order is 1 = Add Room (first bookable card only, every order must be
// unique), 2 = Proceed. Never on the final stage's pay / complete button,
// or the bot would place a booking.
export const criticalPath = (order) => ({
  "data-nav-criticalpath": "true",
  "data-nav-interactiontype": "CLICK",
  "data-nav-interactionorder": String(order),
});

// skeleton shown while hotel / rooms / rates load; the bot waits for it
// to go away before clicking anything
export const LOADING_INDICATOR = { "data-nav-loading-indicator": "true" };

// root wrapper of the booking page: the room list is the landing stage; the
// guest details + booking summary screen is the final checkout stage and the
// root of the microdata, typed both Hotel and LodgingReservation so the
// guest occupancy (RootOccupancy) belongs to the reservation itself
export const bookingStageProps = (isFinal) =>
  isFinal
    ? {
        "data-nav-stage": "checkout",
        "data-nav-stage-final": "true",
        itemScope: true,
        itemType: "https://schema.org/Hotel https://schema.org/LodgingReservation",
      }
    : { "data-nav-stage": "landing", "data-nav-stage-final": "false" };

// guests of the booked room on the root reservation; must sit outside the
// nested STAY_OFFER element, or the props would belong to the offer
export function RootOccupancy({ numAdults, numChildren }) {
  return (
    <>
      <meta itemProp="numAdults" content={String(numAdults ?? 0)} />
      <meta itemProp="numChildren" content={String(numChildren ?? 0)} />
    </>
  );
}

// the stay being booked, nested in the Hotel
export const STAY_OFFER = {
  itemProp: "makesOffer",
  itemScope: true,
  itemType: "https://schema.org/Offer https://schema.org/LodgingReservation",
};

// check-in / check-out of the offer: the guest sees the date, Google reads
// the date-time (same 12:30 the reservation is saved with)
export function StayTime({ itemProp, date, children }) {
  const value = `${moment(date).format("YYYY-MM-DD")}T12:30:00`;
  return (
    <time itemProp={itemProp} dateTime={value} content={value}>
      {children}
    </time>
  );
}

// the whole Price Summary block of the offer: its own price is the stay
// total (tax included), the Room Total / Tax rows are its components
export const PRICE_SUMMARY = {
  itemProp: "priceSpecification",
  itemScope: true,
  itemType: "https://schema.org/CompoundPriceSpecification",
};

// one row of the Price Summary (Room Total, Tax)
export const PRICE_COMPONENT = {
  itemProp: "priceComponent",
  itemScope: true,
  itemType: "https://schema.org/UnitPriceSpecification",
};

// symbol + amount as the guest sees it; with markup the crawler reads a
// plain number and the currency code, the symbol stays outside the price
export function Price({ symbol, amount, currency, markup }) {
  const value = Number(amount).toFixed(2);
  if (!markup) {
    return (
      <>
        {symbol}
        {value}
      </>
    );
  }
  return (
    <>
      <span itemProp="priceCurrency" content={currency}>
        {symbol}
      </span>
      <span itemProp="price">{value}</span>
    </>
  );
}
